import { Link } from 'react-router-dom';
import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { SEOHead } from '../../components/SEO/SEOHead';
import { StructuredData } from '../../components/SEO/StructuredData';
import { createOrganizationSchema, createBreadcrumbListSchema } from '../../components/SEO/StructuredData';

export const AboutPage = () => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://simcarlo.com';

  return (
    <div className="marketing-page">
      <SEOHead
        metadata={{
          title: 'About Us - Making Financial Modeling Accessible',
          description: 'Learn about Simcarlo\'s mission to make Monte Carlo simulation accessible to everyone. Discover our story, values, and commitment to simplifying financial modeling.',
          keywords: ['about Simcarlo', 'company', 'mission', 'financial modeling', 'Monte Carlo simulation'],
          ogImage: `${baseUrl}/images/Landing Main.png`,
          canonicalUrl: `${baseUrl}/about`,
        }}
      />
      <StructuredData
        schemas={[
          createOrganizationSchema('Simcarlo', baseUrl, {
            logo: `${baseUrl}/images/Simcarlo Logo.png`,
            description: 'Making financial modeling accessible to everyone through visual Monte Carlo simulation',
            email: 'hello@simcarlo.com',
          }),
          createBreadcrumbListSchema([
            { name: 'Home', url: baseUrl },
            { name: 'About', url: `${baseUrl}/about` },
          ]),
        ]}
      />
      <MarketingHeader />

      {/* Hero Section */}
      <section className="marketing-hero">
        <div className="marketing-container">
          <div className="marketing-hero-content">
            <div className="marketing-hero-text">
              <h1 className="marketing-hero-title">About Simcarlo</h1>
              <p className="marketing-hero-subtitle">Making financial modeling accessible to everyone</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="marketing-section">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Our Mission</h2>
          <p className="marketing-section-description">
            We believe that financial modeling shouldn't require a PhD in statistics or years of spreadsheet experience. 
            Simcarlo was born from the frustration of seeing great ideas fail because decision-makers couldn't properly 
            model uncertainty and risk.
          </p>
          <p className="marketing-section-description">
            Our goal is to provide a tool for Monte Carlo simulations, making it as easy to use as a calculator but as powerful as enterprise-grade modeling software. We're building tools that help people make better decisions by understanding the full range of possible outcomes, not just single-point estimates.
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="marketing-section">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Our Story</h2>
          <p className="marketing-section-description">
            Simcarlo started when our founders were working on complex financial models for agricultural projects. 
            They found themselves constantly switching between spreadsheets, statistical software, and custom scripts 
            just to run Monte Carlo simulations.
          </p>
          <p className="marketing-section-description">
            Frustrated by the complexity, they set out to build a better way. The result is Simcarlo: a visual, 
            intuitive platform that lets anyone build sophisticated financial models and run thousands of simulations 
            with just a few clicks.
          </p>
        </div>
      </section>

      {/* Values Section */}
      <section className="marketing-section">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Our Values</h2>
          <div className="marketing-faq-grid">
            <div className="marketing-faq-item">
              <h3>Simplicity</h3>
              <p>Complex problems deserve simple solutions. We believe powerful tools should be easy to use.</p>
            </div>
            <div className="marketing-faq-item">
              <h3>Transparency</h3>
              <p>No hidden costs, no surprise fees. What you see is what you get, always.</p>
            </div>
            <div className="marketing-faq-item">
              <h3>Empowerment</h3>
              <p>We give you the tools to make better decisions, not just the answers.</p>
            </div>
            <div className="marketing-faq-item">
              <h3>Innovation</h3>
              <p>We're constantly improving, adding features, and listening to our users' feedback.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="marketing-faq-cta">
        <div className="marketing-container">
          <div className="marketing-cta-content">
            <h2 className="marketing-section-title">Join Us on This Journey</h2>
            <p className="marketing-section-description">Ready to start modeling with confidence?</p>
            <div className="marketing-cta-buttons">
              <Link to="/signup" className="marketing-btn marketing-btn-primary marketing-btn-large">
                Get Started Free
              </Link>
              <Link to="/pricing" className="marketing-btn marketing-btn-secondary marketing-btn-large">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

